import { createContext, useContext, useState, type ReactNode, useEffect, useCallback } from "react"
import { authService } from "@/api/services"
import type { UserResponseDto } from "@/api/types/auth.types"

const STORAGE_KEY_TOKEN = "auth_token"
const STORAGE_KEY_USER = "auth_user"

interface AuthContextType {
  user: UserResponseDto | null
  token: string | null
  isAuthenticated: boolean
  isAdmin: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  updateUser: (userData: UserResponseDto) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserResponseDto | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Initialize auth state from localStorage
  useEffect(() => {
    try {
      const storedToken = localStorage.getItem(STORAGE_KEY_TOKEN)
      const storedUser = localStorage.getItem(STORAGE_KEY_USER)

      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser)

        // Only restore session if user is Admin
        if (parsedUser.userType === "Admin") {
          setToken(storedToken)
          setUser(parsedUser)
        } else {
          // Clear non-admin session
          localStorage.removeItem(STORAGE_KEY_TOKEN)
          localStorage.removeItem(STORAGE_KEY_USER)
        }
      }
    } catch {
      // Clear corrupted localStorage data
      localStorage.removeItem(STORAGE_KEY_TOKEN)
      localStorage.removeItem(STORAGE_KEY_USER)
    }
    setIsLoading(false)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true)
    
    try {
      // Call real API
      const response = await authService.login({ email, password })
      
      // Check if user is Admin
      if (response.user.userType !== "Admin") {
        // Clear any stored data
        localStorage.removeItem(STORAGE_KEY_TOKEN)
        localStorage.removeItem(STORAGE_KEY_USER)
        setIsLoading(false)
        throw new Error("Access denied. Only administrators can access this panel.")
      }
      
      // Store in state
      setToken(response.accessToken)
      setUser(response.user)
      
      setIsLoading(false)
    } catch (error: any) {
      setIsLoading(false)
      
      // Extract error message from API response
      // API returns: { statusCode, message, timestamp, path }
      const apiMessage = error.response?.data?.message
      const errorMessage = apiMessage || error.message || "Invalid login credentials"
      
      throw new Error(errorMessage)
    }
  }, [])

  const logout = useCallback(() => {
    setUser(null)
    setToken(null)
    localStorage.removeItem(STORAGE_KEY_TOKEN)
    localStorage.removeItem(STORAGE_KEY_USER)
  }, [])

  const updateUser = useCallback((userData: UserResponseDto) => {
    setUser(userData)
    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(userData))
  }, [])

  const isAuthenticated = !!token
  const isAdmin = user?.userType === "Admin"

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        token, 
        isAuthenticated, 
        isAdmin,
        isLoading, 
        login, 
        logout,
        updateUser
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return context
}
